// biome-ignore-all lint/performance/noAccumulatingSpread: TODO - address later
// biome-ignore-all lint/suspicious/useIterableCallbackReturn: TODO - address later
import type { FilterValue } from "@/components/filter-popover";
import type { BadgeProps } from "@/components/ui/badge";
import type { Activity } from "@/hooks/api/use-account-activities";
import type { StageTransition } from "@/hooks/api/use-account-opportunities";
import type { TaskGroup } from "@/hooks/api/use-milestone-analyses";
import {
  extractArrayFromFilter,
  extractTextFromFilter,
  isFilterValueActive,
} from "@/lib/filter-value-utils";
import { MIN_DAYS_FOR_GAP, statusFormattingCache } from "./constants";
import { isGroupedActivity } from "./side-sheet";
import type {
  CollapsedActivity,
  FilterMode,
  GapRow,
  OpportunityTransitionItem,
} from "./types";

// Name to ID mapping - will be set by the component
let personNameToIdMapping: Map<string, string> = new Map();

// Function to set the mapping - call this from the component
export function setPersonNameToIdMapping(mapping: Map<string, string>) {
  personNameToIdMapping = mapping;
}

/**
 * Gets a consistent team variant based on the team name
 * @param teamName The name of the team
 * @returns The badge variant to use for the team
 */
export function getTeamVariant(teamName: string): BadgeProps["variant"] {
  if (teamName === "team_unknown" || teamName === "UNKNOWN" || !teamName) {
    return "team-unknown";
  }

  // List of available team color variants
  const variants = [
    "team-sky",
    "team-emerald",
    "team-violet",
    "team-amber",
    "team-indigo",
    "team-teal",
    "team-rose",
    "team-blue",
  ] as const;

  // Create a hash of the string to consistently select a variant
  let hash = 0;
  for (let i = 0; i < teamName.length; i++) {
    hash = teamName.charCodeAt(i) + ((hash << 5) - hash);
  }

  // Use the hash to select a variant
  const index = Math.abs(hash) % variants.length;
  return variants[index];
}

/**
 * Formats a status string to be more readable
 * @param status The status string to format
 * @returns The formatted status string
 */
export function formatStatusText(status: string): string {
  if (!status) return "Unknown";

  // Check cache first
  const cached = statusFormattingCache.get(status);
  if (cached) return cached;

  // Format the status text
  const formatted = status
    .toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

  // Cache the result
  statusFormattingCache.set(status, formatted);

  return formatted;
}

/**
 * Determines if two activities are similar enough to be grouped
 * @param a First activity
 * @param b Second activity
 * @returns True if the activities are similar
 */
export function isSimilarActivity(a: Activity, b: Activity): boolean {
  // Never collapse opportunity transitions
  if ("isOpportunityTransition" in a || "isOpportunityTransition" in b) {
    return false;
  }

  // If either activity has a thread_id, only consider them similar if both have the same non-null thread_id
  if (a.thread_id || b.thread_id) {
    return (
      Boolean(a.thread_id) &&
      Boolean(b.thread_id) &&
      a.thread_id === b.thread_id
    );
  }

  return (
    (Boolean(a.campaign_id) && a.campaign_id === b.campaign_id) ||
    a.activity === b.activity
  );
}

/**
 * Creates a collapsed group from a list of activities
 * @param activities The activities to collapse
 * @returns A collapsed activity object
 */
export function createCollapsedGroup(
  activities: Activity[],
): CollapsedActivity {
  const firstActivity = activities[0];
  const uniqueTeamIds = new Set<string>();
  // Track unique channels
  const uniqueChannels = new Set<string>();

  const startDate = activities[0].date;
  const endDate = activities[activities.length - 1].date;

  activities.forEach((activity) => {
    if (activity.involved_team_ids) {
      activity.involved_team_ids.forEach((id) => uniqueTeamIds.add(id));
    }
    // Add each activity's channel to the set
    if (activity.channel) {
      uniqueChannels.add(activity.channel);
    }
  });

  // Track unique statuses
  const uniqueStatuses = new Set<string>();
  activities.forEach((activity) => {
    if (activity.status) {
      uniqueStatuses.add(activity.status);
    }
  });

  return {
    id: `group-${firstActivity.id}`,
    activities: [...activities],
    date: startDate,
    endDate: endDate,
    activity: firstActivity.activity,
    // Join all unique channels with comma separation
    channel: Array.from(uniqueChannels).join(", "),
    // Join all unique statuses with comma separation
    status: Array.from(uniqueStatuses).join(", "),
    involved_team_ids: Array.from(uniqueTeamIds),
    people: Array.from(
      new Set(
        activities
          .flatMap((a) => a.people || [])
          .filter((p): p is NonNullable<typeof p> => p != null)
          .map((p) => p.id),
      ),
    ).map(
      (id) =>
        activities
          .flatMap((a) => a.people || [])
          .filter((p): p is NonNullable<typeof p> => p != null)
          .find((p) => p.id === id)!,
    ),
    isExpanded: false,
    direction: activities.some((activity) => activity.direction === "IN")
      ? "IN"
      : "OUT",
    // Add the missing fields from the first activity
    source_record_type: firstActivity.source_record_type,
    source_record_id: firstActivity.source_record_id,
    thread_id: firstActivity.thread_id,
    session_id: firstActivity.session_id,
  };
}

/**
 * Collapses consecutive similar activities into groups
 * @param activities The activities to collapse
 * @returns An array of activities and collapsed activity groups
 */
export function collapseConsecutiveActivities(
  activities: Activity[],
): (Activity | CollapsedActivity)[] {
  const { result, currentGroup } = activities.reduce<{
    result: (Activity | CollapsedActivity)[];
    currentGroup: Activity[];
  }>(
    (acc, activity) => {
      const { result, currentGroup } = acc;

      if (currentGroup.length === 0) {
        return { result, currentGroup: [activity] };
      }

      if (isSimilarActivity(currentGroup[0], activity)) {
        return { result, currentGroup: [...currentGroup, activity] };
      }

      const newResult = [
        ...result,
        currentGroup.length === 1
          ? {
              ...currentGroup[0],
              id: currentGroup[0].id,
              people: currentGroup[0].people || [],
            }
          : createCollapsedGroup(currentGroup),
      ];

      return { result: newResult, currentGroup: [activity] };
    },
    { result: [], currentGroup: [] },
  );

  // Handle the last group
  return result.concat(
    currentGroup.length === 0
      ? []
      : currentGroup.length === 1
        ? [
            {
              ...currentGroup[0],
              id: currentGroup[0].id,
              people: currentGroup[0].people || [],
            },
          ]
        : [createCollapsedGroup(currentGroup)],
  );
}

/**
 * Checks if an activity matches the dynamic filters
 */
export function matchesDynamicFilters(
  item: Activity | CollapsedActivity,
  filters: Record<string, FilterValue>,
): boolean {
  if (!item) return false;

  // Count active filters (non-null, non-empty)
  const activeFilters = Object.entries(filters).filter(([, value]) => {
    if (value === null || value === undefined) return false;
    if (typeof value === "string" && value.trim() === "") return false;
    if (Array.isArray(value) && value.length === 0) return false;
    return true;
  });

  // Early return if no active filters
  if (activeFilters.length === 0) {
    return true;
  }

  // Check each active filter
  for (const [key, value] of activeFilters) {
    // Skip null/undefined/empty filters
    if (!value) continue;

    switch (key) {
      // Special filter for cited touchpoints
      case "citedTouchpointIds":
        if (Array.isArray(value) && value.length > 0) {
          // For regular activities, check if its ID is in the list
          if (!isGroupedActivity(item) && !value.includes(item.id)) {
            return false;
          }
          // For collapsed activities, check if any of its activities have IDs in the list
          if (isGroupedActivity(item)) {
            const hasMatch = item.activities.some((activity) =>
              value.includes(activity.id),
            );
            if (!hasMatch) {
              return false;
            }
          }
        }
        break;

      case "activity": {
        // Text filter for activity content using utility function
        const searchText = extractTextFromFilter(value);
        if (searchText) {
          const activityText = (item.activity || "").toLowerCase();
          if (!activityText.includes(searchText.toLowerCase())) {
            return false;
          }
        }
        break;
      }

      case "people": {
        // Filter can be either array (FilterSelect) or string (old FilterPopover)
        const peopleArray = extractArrayFromFilter(value);
        if (peopleArray.length > 0) {
          const itemPeople = item.people || [];
          // Convert person names to IDs using the mapping
          const personIds = peopleArray.map(
            (personName) => personNameToIdMapping.get(personName) || personName,
          );
          // Check if any person in the activity matches the IDs
          const peopleMatch = itemPeople.some((person) =>
            personIds.includes(person.id),
          );
          if (!peopleMatch) {
            return false;
          }
        } else if (typeof value === "string" && value.trim()) {
          // Handle single string value (from old FilterPopover)
          const personId = personNameToIdMapping.get(value) || value;
          const itemPeople = item.people || [];
          if (!itemPeople.some((person) => person.id === personId)) {
            return false;
          }
        }
        break;
      }

      case "channel": {
        // Filter can be either array (FilterSelect) or string (old FilterPopover)
        const channelArray = extractArrayFromFilter(value);
        if (channelArray.length > 0) {
          if (!channelArray.includes(item.channel)) {
            return false;
          }
        } else if (typeof value === "string" && value.trim()) {
          // Single string value
          if (item.channel !== value) {
            return false;
          }
        }
        break;
      }

      case "team": {
        // Filter can be either array (FilterSelect) or string (old FilterPopover)
        const teamArray = extractArrayFromFilter(value);
        if (teamArray.length > 0) {
          if (
            !item.involved_team_ids ||
            !item.involved_team_ids.some((teamId) => teamArray.includes(teamId))
          ) {
            return false;
          }
        } else if (typeof value === "string" && value.trim()) {
          // Single string value
          if (
            !item.involved_team_ids ||
            !item.involved_team_ids.includes(value)
          ) {
            return false;
          }
        }
        break;
      }

      case "status": {
        // Filter can be either array (FilterSelect) or string (old FilterPopover)
        const statusArray = extractArrayFromFilter(value);
        if (statusArray.length > 0) {
          if (!item.status || !statusArray.includes(item.status)) {
            return false;
          }
        } else if (typeof value === "string" && value.trim()) {
          // Single string value
          if (item.status !== value) {
            return false;
          }
        }
        break;
      }

      case "direction": {
        // Filter can be either array (FilterSelect) or string (old FilterPopover)
        const directionArray = extractArrayFromFilter(value);
        if (directionArray.length > 0) {
          if (!item.direction || !directionArray.includes(item.direction)) {
            return false;
          }
        } else if (typeof value === "string" && value.trim()) {
          // Single string value
          if (item.direction !== value) {
            return false;
          }
        }
        break;
      }

      // For other column types
      default:
        // For any other filters we don't explicitly handle, just log them
        break;
    }
  }

  // If we get here, all active filters matched
  return true;
}

/**
 * Checks if there are any meaningful active filters
 */
export function hasActiveFilters(filters: Record<string, unknown>): boolean {
  // Count non-empty, meaningful filters using the centralized utility
  return Object.values(filters).some((value) =>
    isFilterValueActive(value as FilterValue),
  );
}

/**
 * Filters activity data based on filter criteria
 */
export function filterActivityData(
  data: Activity[],
  filterMode: FilterMode,
  filters: Record<string, unknown>,
): Activity[] {
  // Check if there are any meaningful active filters
  const isFiltering = hasActiveFilters(filters);

  // If there are no active filters, return all data
  if (!isFiltering) {
    return data;
  }

  // Early return if not in "only-show" mode
  if (filterMode !== "only-show") return data;

  // Use the dynamic filter matching
  const filteredData = data.filter((item) =>
    matchesDynamicFilters(item, filters as Record<string, FilterValue>),
  );

  return filteredData;
}

/**
 * Processes opportunity transitions into a standardized format
 * @param opportunities Array of opportunities with stage transitions
 * @returns Array of processed opportunity transitions
 */
export function processOpportunityTransitions(
  opportunities: Array<{
    id: string;
    name: string;
    stage_transitions: StageTransition[];
  }> = [],
) {
  if (!opportunities) return [];

  return opportunities.flatMap((opportunity) => {
    const stageTransitions = opportunity.stage_transitions;
    if (!stageTransitions) return [];

    return stageTransitions.map((transition: StageTransition) => ({
      ...transition,
      opportunityName: opportunity.name,
      opportunityId: opportunity.id,
      timestamp: transition.timestamp,
      // Add a property for milestone analysis (will be populated later)
      MilestoneAnalysis: null,
    }));
  });
}

/**
 * Merges opportunity transitions with activity data
 * @param filteredData The filtered activity data
 * @param stageTransitions Array of stage transitions
 * @returns Merged array of activities and transitions, sorted by date
 */
export function mergeTransitionsWithActivities(
  filteredData: Activity[],
  stageTransitions: Array<{
    timestamp: string;
    opportunityId: string;
    opportunityName: string;
    MilestoneAnalysis?: TaskGroup | null;
    [key: string]: unknown;
  }> = [],
): Activity[] {
  if (!filteredData.length || !stageTransitions.length) {
    return filteredData;
  }

  // Create a copy of filtered data for merging
  const result = [...filteredData];

  // Insert stage transitions at appropriate positions based on timestamp
  stageTransitions.forEach((transition) => {
    // Create a special marker object for the transition with proper typing
    result.push({
      id: `transition-${transition.opportunityId}-${transition.timestamp}`,
      date: transition.timestamp,
      isOpportunityTransition: true,
      opportunityTransition: {
        ...transition,
        // Ensure MilestoneAnalysis is preserved
        MilestoneAnalysis: transition.MilestoneAnalysis,
      },
    } as unknown as Activity);
  });

  return result.sort((a, b) => {
    const dateA = new Date(a.date);
    const dateB = new Date(b.date);
    return dateA.getTime() - dateB.getTime();
  });
}

/**
 * Calculates the number of days between two activities
 * @param currentItem The current activity or collapsed activity
 * @param previousItem The previous activity or collapsed activity
 * @param index The index of the current item in the array
 * @returns Number of days between the activities
 */
export function calculateDaysBetween(
  currentItem: Activity | CollapsedActivity,
  previousItem:
    | Activity
    | CollapsedActivity
    | OpportunityTransitionItem
    | undefined,
  index: number,
): number {
  if (index === 0) return 0;
  if (!previousItem) return 0;

  // Skip calculation if previous item is a transition
  if ("isOpportunityTransition" in previousItem) {
    return 0;
  }

  const currentDate = new Date(currentItem.date);

  // For collapsed groups, use the endDate (which is the latest date) for the previous item
  const previousDate =
    "activities" in previousItem && previousItem.endDate
      ? new Date(previousItem.endDate)
      : new Date(previousItem.date);

  // Calculate days between the previous item's end date and the current item's start date
  const timeDiff = currentDate.getTime() - previousDate.getTime();
  return Math.floor(Math.abs(timeDiff) / (1000 * 60 * 60 * 24));
}

/**
 * Maps filter values to display values (particularly for IDs that need to be shown as names)
 * @param filters The original filters object
 * @param getPersonName Function to get person name from ID
 * @param getTeamDisplayName Function to get team display name from ID
 * @returns A new filters object with mapped display values
 */
export function mapDisplayFilters(
  filters: Record<string, FilterValue>,
  getPersonName: (id: string) => string,
  getTeamDisplayName: (id: string) => string,
): Record<string, FilterValue> {
  return Object.entries(filters).reduce((acc, [key, value]) => {
    // Skip null or empty values
    if (value === null || (Array.isArray(value) && value.length === 0)) {
      return acc;
    }

    // Handle array values (like selected people or teams)
    if (Array.isArray(value)) {
      // Map person IDs to names for people filter
      if (key === "people") {
        return {
          ...acc,
          [key]: value.map((id) => getPersonName(id)),
        };
      }

      // Map team IDs to display names for team filter
      if (key === "team") {
        return {
          ...acc,
          [key]: value.map((id) => getTeamDisplayName(id)),
        };
      }

      // Format status values using formatStatusText if needed
      if (key === "status") {
        return {
          ...acc,
          [key]: value,
        };
      }
    }

    // For all other filters, keep the original value
    return {
      ...acc,
      [key]: value,
    };
  }, {});
}

/**
 * Associates milestone analysis with opportunity stage transitions
 * @param stageTransitions Array of stage transitions
 * @param MilestoneAnalyses Array of milestone analysis task groups
 * @returns Array of stage transitions with associated milestone analysis
 */
export function associateMilestoneAnalysesWithTransitions(
  stageTransitions: Array<{
    timestamp: string;
    opportunityId: string;
    opportunityName: string;
    [key: string]: unknown;
  }>,
  MilestoneAnalyses?: TaskGroup[],
) {
  if (!MilestoneAnalyses?.length || !stageTransitions?.length) {
    return stageTransitions;
  }

  // Create a map of milestone analysis by opportunity ID for faster lookup
  const momentsByOpportunity = new Map<string, TaskGroup[]>();
  MilestoneAnalyses.forEach((taskGroup) => {
    // Add the taskGroup directly to the map by opportunity_id
    if (!momentsByOpportunity.has(taskGroup.opportunity_id)) {
      momentsByOpportunity.set(taskGroup.opportunity_id, []);
    }
    momentsByOpportunity.get(taskGroup.opportunity_id)!.push(taskGroup);
  });

  // Track which task groups have already been associated with a transition
  // to ensure each task group is only matched once
  const usedTaskGroups = new Set<string>();

  // Sort transitions by date (earliest first) to ensure we associate with the first matching transition
  const sortedTransitions = [...stageTransitions].sort((a, b) => {
    return new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime();
  });

  // Process transitions in order
  return sortedTransitions.map((transition) => {
    const opportunityMoments =
      momentsByOpportunity.get(transition.opportunityId) || [];

    // Find a task group that matches both opportunity ID and date (within 2 days)
    // AND has not been used yet
    const relatedTaskGroup = opportunityMoments.find((taskGroup) => {
      // Skip if this task group has already been used
      if (usedTaskGroups.has(taskGroup.task_id)) {
        return false;
      }

      // Parse the timestamps to compare dates
      const transitionTimestamp = new Date(transition.timestamp).getTime();
      const opportunityCreatedTimestamp = new Date(
        taskGroup.opportunity_created_date,
      ).getTime();

      // Calculate the time difference in days
      const daysDifference =
        Math.abs(transitionTimestamp - opportunityCreatedTimestamp) /
        (1000 * 60 * 60 * 24);

      // Match if opportunity_id matches and dates are within 2 days of each other
      return (
        taskGroup.opportunity_id === transition.opportunityId &&
        daysDifference <= 2
      ); // Allow up to 2 days difference
    });

    // If we found a matching task group, mark it as used
    if (relatedTaskGroup) {
      usedTaskGroups.add(relatedTaskGroup.task_id);
    }

    return {
      ...transition,
      MilestoneAnalysis: relatedTaskGroup || null,
    };
  });
}

export function insertGapRows(
  data: (Activity | CollapsedActivity)[],
): Array<Activity | CollapsedActivity | GapRow> {
  const result: Array<Activity | CollapsedActivity | GapRow> = [];
  data.forEach((item, index) => {
    if (index > 0) {
      const days = calculateDaysBetween(item, data[index - 1], index);
      if (days > MIN_DAYS_FOR_GAP) {
        const gapRow: GapRow = {
          id: `gap-${index}-${item.id}`,
          kind: "gap",
          days,
          date: item.date,
        };
        result.push(gapRow);
      }
    }
    result.push(item);
  });
  return result;
}
